import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Instagram, Facebook, Linkedin, Youtube } from 'lucide-react';
import Logo from '@/components/Logo';
import { useLang } from '@/i18n/LanguageContext';

const SOCIAL_ICONS = { Instagram, Facebook, LinkedIn: Linkedin, YouTube: Youtube };

export default function Footer() {
  const { content, route } = useLang();
  const f = content.footer;
  const legalLinks = [
    { k: 'faq', label: content.faq.title },
    { k: 'privacy', label: content.legal.privacy.title },
    { k: 'terms', label: content.legal.terms.title },
  ];

  return (
    <footer data-testid="site-footer" className="relative overflow-hidden bg-brand-deep text-white">
      <div className="grid-dots absolute inset-0 opacity-25" />
      <div className="container-x relative py-16 lg:py-20">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Logo variant="light" />
            <p className="mt-6 max-w-sm text-sm leading-relaxed text-white/60">{f.description}</p>
            <div className="mt-6 flex gap-3">
              {f.socials.map((s) => {
                const Icon = SOCIAL_ICONS[s];
                return (
                  <a
                    key={s}
                    href={f.instagramUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={s}
                    data-testid={`footer-social-${s.toLowerCase()}`}
                    className="rounded-full border border-white/15 p-2.5 text-white/70 transition hover:border-brand-blue hover:text-brand-blue"
                  >
                    {Icon && <Icon className="h-4 w-4" />}
                  </a>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-3">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-cyan">{f.institucionalTitle}</h4>
            <ul className="mt-5 space-y-2.5">
              {content.nav.map((item) => (
                <li key={item.k}>
                  <Link to={route(item.k)} data-testid={`footer-nav-${item.k}`} className="text-sm text-white/70 transition hover:text-white">
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-4">
            <h4 className="text-[11px] font-semibold uppercase tracking-[0.22em] text-brand-cyan">{f.contactTitle}</h4>
            <ul className="mt-5 space-y-3.5 text-sm text-white/70">
              <li className="flex gap-3">
                <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />
                <span>{f.address}</span>
              </li>
              <li className="flex gap-3">
                <Phone className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />
                <a href={`tel:${f.phone.replace(/\D/g, '')}`} data-testid="footer-phone-link" className="transition hover:text-white">{f.phone}</a>
              </li>
              <li className="flex gap-3">
                <Mail className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />
                <a href={`mailto:${f.email}`} data-testid="footer-email-link" className="transition hover:text-white">{f.email}</a>
              </li>
            </ul>
            <ul className="mt-6 flex flex-wrap gap-x-5 gap-y-2">
              {legalLinks.map((l) => (
                <li key={l.k}>
                  <Link to={route(l.k)} data-testid={`footer-legal-${l.k}`} className="text-xs text-white/50 transition hover:text-white">
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 flex flex-col items-start justify-between gap-3 border-t border-white/10 pt-6 sm:flex-row sm:items-center">
          <p className="text-xs text-white/40">© {new Date().getFullYear()} {f.rights}</p>
          <p className="font-mono text-[11px] tracking-wide text-white/30">TECNOLOGIA SATELITAL · BR · PY · AR</p>
        </div>
      </div>
    </footer>
  );
}